import React, { useState, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import {
  connectEyeDevice,
  startStreaming,
  disconnectEyeDevice,
} from "../../services/eyeBluetooth.service";
import { getAudioOutputDevices } from "../../services/audioDevices.service";
import { FONT_BODY, FONT_MONO } from "theme/fonts";

// ── Palette ───────────────────────────────────────────────────────────────────
const ACCENT = "#38b5b5";
const FONT   = FONT_BODY;
const MONO   = FONT_MONO;

// Audio file used for the in-panel "preview" play/stop button on the
// Volume Range control (Step 4 → Audio Settings). Lets the user hear the
// affirmation clip at the exact volume their Min/Max sliders are set to,
// without needing to start a full session.
const PREVIEW_AUDIO_SRC    = "/audio/stay-aware.mp3";
const PREVIEW_AUDIO_SRC_2  = "/audio/stay-conscious.mp3";
const STEP_PALETTE = [
  { from: "#378add", to: "#7f77dd" },
  { from: "#d4537e", to: "#9b72dd" },
  { from: "#e07b39", to: "#c090e0" },
  { from: "#3ecb8a", to: "#38b5b5" },   // ← Step 4 green-teal
];

// ── Eye state config ──────────────────────────────────────────────────────────
type EyeStateType = "open" | "half" | "closed" | "dreaming";

const EYE_STATES: { value: EyeStateType; label: string; emoji: string; color: string; desc: string }[] = [
  { value: "open",     label: "Awake",      emoji: "👁",  color: "#4fc3f7", desc: "Waking awareness" },
  { value: "half",     label: "Drowsy",     emoji: "😌",  color: "#9b72dd", desc: "Threshold of sleep" },
  { value: "closed",   label: "Deep Sleep", emoji: "🌑",  color: "#3ecb8a", desc: "The deep void" },
  { value: "dreaming", label: "REM",        emoji: "💤",  color: "#c8a050", desc: "Conscious in REM" },
];

// ── Static styles ─────────────────────────────────────────────────────────────
const S: Record<string, CSSProperties> = {
  sidebar: {
    width:         "100%",
    height:        "100%",
    display:       "flex",
    flexDirection: "column",
    fontFamily:    FONT,
    color:         "#d0e8f8",
    boxSizing:     "border-box",
    overflow:      "hidden",
    background:    "#040f18",
    position:      "relative",
  },
  header: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "clamp(10px,1.1vw,14px) clamp(12px,1.4vw,18px)",
    background:     "#071520",
    borderBottom:   "1px solid rgba(56,181,181,0.22)",
    flexShrink:     0,
  },
  headerTitle: {
    fontFamily: FONT_BODY,
    fontStyle:  "normal",
    fontWeight: 700,
    fontSize:   "clamp(10px,1vw,13px)",
    color:      "#e8f4ff",
  },
  subtitleBar: {
    background:   "#050f1a",
    borderBottom: "1px solid rgba(56,181,181,0.10)",
    padding:      "clamp(4px,0.45vw,6px) clamp(12px,1.4vw,18px)",
    fontSize:     "clamp(7px,0.65vw,9px)",
    fontStyle:    "normal",
    color:        "#7aa8cc",
    fontFamily:   FONT_BODY,
    flexShrink:   0,
  },
  navRow: {
    display:    "flex",
    gap:        5,
    padding:    "clamp(8px,0.9vw,12px) clamp(12px,1.4vw,18px) 0",
    flexShrink: 0,
  },
  scrollArea: {
    flex:      1,
    overflowY: "auto" as const,
    padding:   "clamp(8px,0.9vw,12px) clamp(10px,1.2vw,16px)",
  },
  footer: {
    display:        "flex",
    justifyContent: "space-between",
    alignItems:     "center",
    padding:        "clamp(8px,0.9vw,12px) clamp(12px,1.4vw,18px)",
    borderTop:      "1px solid rgba(56,181,181,0.12)",
    background:     "#050f1a",
    flexShrink:     0,
  },
};

// ── Types ─────────────────────────────────────────────────────────────────────
type HzData = number | { ch1: number; ch2: number; ch3: number };

type ToastType = "success" | "error" | "info" | "warning" | "bluetooth";

interface ToastOptions {
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
}

interface AudioDevice {
  deviceId: string;
  label: string;
}

interface SetupPaneProps {
  onHzChange: (data: HzData) => void;
  onConnectionChange: (status: boolean) => void;
  toast: (options: ToastOptions) => void;
  selectedDeviceIds: string[];
  onDeviceSelectionChange: (ids: string[]) => void;
  onBeginSession?: () => void;
  onEndSession?: () => void;
  sessionActive?: boolean;
  showPractice?: boolean;
  /* Affirmation interval — new in Step 4 */
  affirmationInterval?: number;
  onAffirmationIntervalChange?: (secs: number) => void;
  /* Admin panel props */
  isAdmin?: boolean;
  practiceCompleted?: boolean;
  autoAffirmation?: boolean;
  onAutoAffirmationChange?: (v: boolean) => void;
  remThreshold?: number;
  onRemThresholdChange?: (v: number) => void;
  adminEyeOverride?: EyeStateType | null;
  onAdminEyeOverrideChange?: (state: EyeStateType | null) => void;
  dreamingActive?: boolean;
  dreamAudioPaused?: boolean;
  onPlayAffirmationNow?: () => void;
  onStopAffirmation?: () => void;
  dataEyeState?: EyeStateType;
  /* Debug toggle */
  remDebugEnabled?: boolean;
  onRemDebugToggle?: (v: boolean) => void;
  audioMinGain?: number;        // 0.0 – 1.0, default 0.015
  audioMaxGain?: number;        // 0.0 – 1.0, default 0.05
  onAudioMinGainChange?: (v: number) => void;
  onAudioMaxGainChange?: (v: number) => void;
  /* Advanced view toggle — moved here from Header */
  bothConnected?: boolean;
  advancedView?: boolean;
  onManualDisconnectStart?: () => void;
  onToggleAdvanced?: (val: boolean) => void;
}

// ── Signal check constants ────────────────────────────────────────────────────
const SIGNAL_THRESHOLD = 5;
const REF_THRESHOLD    = 2;
const STALE_MS         = 20_000;

// ── useChannelAlive ───────────────────────────────────────────────────────────
function useChannelAlive(value: number, threshold: number, connected: boolean): boolean {
  const lastSeenRef = useRef<number | null>(null);
  const [alive, setAlive] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!connected) { lastSeenRef.current = null; setAlive(false); return; }
    if (Math.abs(value) > threshold) lastSeenRef.current = Date.now();
  }, [value, threshold, connected]);

  useEffect(() => {
    if (!connected) { setAlive(false); return; }
    intervalRef.current = setInterval(() => {
      setAlive(lastSeenRef.current !== null && Date.now() - lastSeenRef.current < STALE_MS);
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [connected]);

  useEffect(() => {
    if (!connected) { lastSeenRef.current = null; setAlive(false); }
  }, [connected]);

  return alive;
}

// ── useStaleSeconds ───────────────────────────────────────────────────────────
function useStaleSeconds(alive: boolean, connected: boolean): number {
  const [secs, setSecs] = useState(0);
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    if (!connected || alive) { startRef.current = null; setSecs(0); return; }
    if (startRef.current === null) startRef.current = Date.now();
    const id = setInterval(() => {
      setSecs(Math.floor((Date.now() - (startRef.current ?? Date.now())) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [alive, connected]);
  return secs;
}

// ── useActivityState ──────────────────────────────────────────────────────────
function useActivityState(value: number, enabled: boolean) {
  const [active, setActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevRef  = useRef<number>(value);
  useEffect(() => {
    if (!enabled) { setActive(false); return; }
    if (value !== prevRef.current) {
      prevRef.current = value;
      setActive(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setActive(false), 600);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, enabled]);
  return active;
}

// ── ElectrodeNode (SVG) ───────────────────────────────────────────────────────
interface ElectrodeConfig { id: string; label: string; cx: number; cy: number; }
const ELECTRODES: ElectrodeConfig[] = [
  { id: "left",  label: "L−",  cx: 60,  cy: 80 },
  { id: "right", label: "R+",  cx: 140, cy: 80 },
  { id: "ref",   label: "REF", cx: 100, cy: 30 },
];

function ElectrodeNode({ electrode, active, connected, alive }: {
  electrode: ElectrodeConfig; active: boolean; connected: boolean; alive: boolean;
}) {
  const color = !connected ? "#ef4444" : !alive ? "#f59e0b" : active ? "#4ade80" : "#38bdf8";
  return (
    <g>
      <circle cx={electrode.cx} cy={electrode.cy} r="8" fill="#0a1628" stroke={color} strokeWidth="2" />
      <circle cx={electrode.cx} cy={electrode.cy} r="3" fill={color} />
      <text x={electrode.cx} y={electrode.cy + 18} textAnchor="middle"
        fill={color} fontSize="9" fontFamily={FONT_MONO}>
        {electrode.label}
      </text>
      {connected && alive && active && (
        <circle cx={electrode.cx} cy={electrode.cy} r="10"
          fill="none" stroke="#4ade80" strokeWidth="2" opacity="0.6"
          style={{ animation: "eogPulse 0.6s ease-out forwards" }} />
      )}
      {connected && !alive && (
        <circle cx={electrode.cx} cy={electrode.cy} r="12"
          fill="none" stroke="#f59e0b" strokeWidth="1"
          strokeDasharray="3 3" opacity="0.6" />
      )}
    </g>
  );
}

// ── Step 3 Signal Check Panel ─────────────────────────────────────────────────
interface SignalCheckPanelProps {
  connected: boolean;
  amplitude: { ch1: number; ch2: number; ch3: number };
  accentFrom: string;
  accentTo:   string;
  onSignalValid: () => void;
  onSignalInvalid: () => void;
}

const SignalCheckPanel: React.FC<SignalCheckPanelProps> = ({
  connected, amplitude, accentFrom, accentTo, onSignalValid, onSignalInvalid,
}) => {
  const ch1 = amplitude.ch1;
  const ref = amplitude.ch3;

  const ch1Alive = useChannelAlive(ch1, SIGNAL_THRESHOLD, connected);
  const refAlive = useChannelAlive(ref, REF_THRESHOLD, connected);
  const ch1Stale = useStaleSeconds(ch1Alive, connected);
  const refStale = useStaleSeconds(refAlive, connected);
  const ch1Active = useActivityState(ch1, connected);
  const refActive = useActivityState(ref, connected);

  const signalValid = connected
    && ch1Alive
    && refAlive
    && Math.abs(ch1) > SIGNAL_THRESHOLD
    && Math.abs(ref) > REF_THRESHOLD;

  const markedRef = useRef(false);
  useEffect(() => {
    if (signalValid && !markedRef.current) {
      markedRef.current = true;
      onSignalValid();
    }
    if (!signalValid) {
      if (markedRef.current) {
        markedRef.current = false;
        onSignalInvalid();
      }
    }
  }, [signalValid, onSignalValid, onSignalInvalid]);

  let statusText  = "DEVICE NOT CONNECTED";
  let statusColor = "#ef4444";
  let statusSub: string | undefined;

  if (connected && !refAlive) {
    statusText  = "REF NOT DETECTED";
    statusColor = "#f59e0b";
    statusSub   = refStale > 0 ? `${refStale}s since last ref signal` : "waiting…";
  } else if (connected && !ch1Alive) {
    statusText  = "SIGNAL LOST";
    statusColor = "#f59e0b";
    statusSub   = ch1Stale > 0 ? `${ch1Stale}s since last signal` : "waiting…";
  } else if (signalValid) {
    statusText  = "VALID SIGNAL ✓";
    statusColor = "#4ade80";
  }

  return (
    <div style={{
      marginTop: 8, borderRadius: 7, overflow: "hidden",
      border: `1px solid ${signalValid ? "#4ade8033" : `${accentFrom}33`}`,
      background: signalValid ? "rgba(74,222,128,0.04)" : `${accentFrom}08`,
      padding: "10px",
      transition: "all 0.4s ease",
    }}>
      <style>{`
        @keyframes eogPulse {
          0%   { r: 10; opacity: 0.6; }
          100% { r: 18; opacity: 0;   }
        }
      `}</style>

      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
        padding: "4px 8px", borderRadius: 6,
        background: `${statusColor}18`, border: `1px solid ${statusColor}33`,
        flexWrap: "wrap",
      }}>
        <div style={{
          width: 6, height: 6, borderRadius: "50%", background: statusColor,
          flexShrink: 0,
          boxShadow: signalValid ? `0 0 6px ${statusColor}` : "none",
          animation: signalValid ? "none" : connected ? "blink 1.2s ease-in-out infinite" : "none",
        }} />
        <span style={{ fontFamily: MONO, fontSize: 9, color: statusColor }}>
          {statusText}
        </span>
        {statusSub && (
          <span style={{ fontFamily: MONO, fontSize: 8, color: `${statusColor}88`, marginLeft: 2 }}>
            {statusSub}
          </span>
        )}
      </div>

      {!connected && (
        <p style={{ fontFamily: MONO, fontSize: 8, color: "#5a7a96", margin: "0 0 8px", fontStyle: "italic" }}>
          Complete Step 1 first — connect your Dream Sensor via Bluetooth.
        </p>
      )}

      {connected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: MONO, fontSize: 9 }}>
          {[
            { label: "CH1", val: ch1, alive: ch1Alive },
            { label: "REF", val: ref, alive: refAlive },
          ].map(({ label, val, alive }) => (
            <div key={label} style={{
              flex: 1, padding: "3px 6px", borderRadius: 4,
              background: alive ? "rgba(74,222,128,0.06)" : "rgba(245,158,11,0.08)",
              border: `1px solid ${alive ? "#4ade8033" : "#f59e0b33"}`,
              color: alive ? "#4ade80" : "#f59e0b",
            }}>
              <span style={{ opacity: 0.6 }}>{label} </span>
              {val.toFixed(0)}
              {!alive && <span style={{ opacity: 0.55 }}> ✗</span>}
            </div>
          ))}
        </div>
      )}

      <svg viewBox="0 0 200 120" width="100%">
        <circle cx="100" cy="65" r="42" fill="none" stroke="#1e3050" strokeWidth="1" />
        <line x1="60"  y1="75" x2="100" y2="28" stroke="#1e3050" strokeWidth="0.8" />
        <line x1="140" y1="75" x2="100" y2="28" stroke="#1e3050" strokeWidth="0.8" />
        <ElectrodeNode electrode={{ ...ELECTRODES[0], cy: 75 }} active={ch1Active} connected={connected} alive={ch1Alive} />
        <ElectrodeNode electrode={{ ...ELECTRODES[1], cy: 75 }} active={ch1Active} connected={connected} alive={ch1Alive} />
        <ElectrodeNode electrode={{ ...ELECTRODES[2], cy: 28 }} active={refActive} connected={connected} alive={refAlive} />
      </svg>

      <div style={{
        display: "flex", gap: 8, marginTop: 4, justifyContent: "center",
        fontFamily: MONO, fontSize: 7, color: "#4a6a8a", flexWrap: "wrap",
      }}>
        {[
          { color: "#4ade80", label: "active"      },
          { color: "#38bdf8", label: "idle"         },
          { color: "#f59e0b", label: "stale/weak"   },
          { color: "#ef4444", label: "disconnected" },
        ].map(({ color, label }) => (
          <span key={label} style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, display: "inline-block" }} />
            {label}
          </span>
        ))}
      </div>

      {connected && !refAlive && refStale >= STALE_MS / 1000 && (
        <div style={{
          marginTop: 6, padding: "4px 8px", borderRadius: 5,
          background: "rgba(239,68,68,0.1)", border: "1px solid #ef444433",
          fontFamily: MONO, fontSize: 8, color: "#ef4444",
        }}>
          REF stale &gt; 20s — check REF pad contact
        </div>
      )}
      {connected && refAlive && !ch1Alive && ch1Stale >= STALE_MS / 1000 && (
        <div style={{
          marginTop: 6, padding: "4px 8px", borderRadius: 5,
          background: "rgba(239,68,68,0.1)", border: "1px solid #ef444433",
          fontFamily: MONO, fontSize: 8, color: "#ef4444",
        }}>
          CH1 stale &gt; 20s — check L− / R+ pads
        </div>
      )}
    </div>
  );
};

// ── SensorConnectButton ───────────────────────────────────────────────────────
interface SensorConnectButtonProps {
  gradientFrom: string; gradientTo: string;
  onHzChange: (data: HzData) => void;
  onConnectionChange: (status: boolean) => void;
  toast: (opts: ToastOptions) => void;
  onConnected: () => void;
  isConnected: boolean;
  onDisconnect: () => void;
  onEndSession?: () => void;
  onManualDisconnectStart?: () => void;
}

const SensorConnectButton: React.FC<SensorConnectButtonProps> = ({
  gradientFrom, gradientTo, onHzChange, onConnectionChange, toast, onConnected,
  isConnected, onDisconnect,
  onEndSession,onManualDisconnectStart,
}) => {
  const [status, setStatus] = useState<"idle" | "connecting" | "connected">(
    isConnected ? "connected" : "idle"
  );
  const deviceRef = useRef<any>(null);
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => {
    if (isConnected && status !== "connected") setStatus("connected");
    if (!isConnected && status === "connected") setStatus("idle");
  }, [isConnected]);

  const handleConnect = async () => {
    if (status !== "idle") return;
    const nav = navigator as any;
    if (!nav.bluetooth) {
      toast({ type: "error", title: "Bluetooth Not Supported", message: "Use Chrome on desktop (Windows/Mac/Linux).", duration: 6000 });
      return;
    }
    setStatus("connecting");
    try {
      const device: any = await connectEyeDevice();
      deviceRef.current = device;
      await startStreaming((data: HzData) => { onHzChange(data); });
      setStatus("connected");
      onConnectionChange(true);
      onConnected();
      toast({ type: "success", title: "Dream Sensor Connected", message: `Connected to: ${device.name || "Unknown Device"}`, duration: 3000 });
      device.addEventListener("gattserverdisconnected", () => {
        if (status === "idle") return;
        setStatus("idle");
        onConnectionChange(false);
        onHzChange(0);
        toast({ type: "warning", title: "Device Disconnected", message: "Bluetooth device lost connection unexpectedly.", duration: 5000 });
      });
    } catch (err: any) {
      setStatus("idle");
      onConnectionChange(false);
      if (err?.name === "NotFoundError" || err?.message?.includes("cancelled") || err?.message?.includes("chooser")) {
        toast({ type: "info", title: "Connection Cancelled", message: "No device was selected.", duration: 3000 }); return;
      }
      if (err?.name === "NotSupportedError" || err?.message?.toLowerCase().includes("bluetooth")) {
        toast({ type: "error", title: "Bluetooth Unavailable", message: "Please enable Bluetooth and try again.", duration: 5000 }); return;
      }
      if (err?.name === "SecurityError") {
        toast({ type: "error", title: "Permission Denied", message: "Bluetooth access was blocked.", duration: 5000 }); return;
      }
      toast({ type: "error", title: "Connection Failed", message: err?.message ?? "Unknown error.", duration: 5000 });
    }
  };
const handleDisconnect = async () => {
  onManualDisconnectStart?.();
  setStatus("idle");
  onConnectionChange(false);
  onHzChange(0);
  onDisconnect();
  try { await disconnectEyeDevice(); } catch {}
  toast({ type: "bluetooth", title: "Disconnected", message: "Dream sensor disconnected.", duration: 2500 });
  onEndSession?.();   // ← ADD THIS LINE
};

  if (isConnected || status === "connected") {
    return (
      <button onClick={handleDisconnect} style={{
        marginTop: 6, width: "100%", padding: "9px 0", borderRadius: 6,
        border: "1px solid #ef444444", background: "rgba(239,68,68,0.08)", color: "#ef4444",
        fontFamily: MONO, fontWeight: 700, fontSize: 9, letterSpacing: 1.5,
        textTransform: "uppercase", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        transition: "all 0.35s ease",
      }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
        Disconnect Sensor
      </button>
    );
  }

  const bg = status === "connecting"
    ? `linear-gradient(90deg, ${gradientFrom}88, ${gradientTo}88)`
    : `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`;

  return (
    <button onClick={handleConnect} disabled={status === "connecting"} style={{
      marginTop: 6, width: "100%", padding: "9px 0", borderRadius: 6,
      border: "none", background: bg, color: "#e8f4ff",
      fontFamily: MONO, fontWeight: 700, fontSize: 9, letterSpacing: 1.5,
      textTransform: "uppercase", cursor: status === "connecting" ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      boxShadow: `0 3px 12px ${gradientFrom}33`,
      transition: "all 0.35s ease",
    }}>
      {status === "idle" && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#e8f4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"/>
          <circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"/>
          <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/>
        </svg>
      )}
      {status === "connecting" && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#e8f4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ animation: "spin 1s linear infinite" }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
      )}
      {status === "idle" && "Connect Sensor via Bluetooth"}
      {status === "connecting" && "Connecting…"}
    </button>
  );
};

// ── SpeakerPanel ──────────────────────────────────────────────────────────────
interface SpeakerPanelProps {
  gradientFrom: string; gradientTo: string;
  selected: string[]; onChange: (ids: string[]) => void;
  toast: (opts: ToastOptions) => void; onConnected: () => void;
}

const SpeakerPanel: React.FC<SpeakerPanelProps> = ({
  gradientFrom, gradientTo, selected, onChange, toast, onConnected,
}) => {
  const [outputs, setOutputs] = useState<AudioDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded]   = useState(false);

  useEffect(() => { if (selected.length > 0) onConnected(); }, []);

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
    if (next.length > 0) onConnected();
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      const devices = await getAudioOutputDevices();
      if (!devices.length) {
        toast({ type: "warning", title: "No Audio Devices Found", message: "Make sure your Bluetooth speakers are paired and connected.", duration: 4000 });
      } else {
        setOutputs(devices); setLoaded(true);
        toast({ type: "success", title: "Devices Loaded", message: `Found ${devices.length} audio output device${devices.length > 1 ? "s" : ""}.`, duration: 2500 });
      }
    } catch (err: any) {
      if (err?.name === "NotAllowedError") {
        toast({ type: "error", title: "Permission Denied", message: "Allow microphone permission to enumerate audio devices.", duration: 5000 });
      } else {
        toast({ type: "error", title: "Failed to Load Devices", message: err?.message ?? "Could not access audio devices.", duration: 4000 });
      }
    } finally { setLoading(false); }
  };

  return (
    <div style={{ marginTop: 6 }}>
      {selected.length > 0 && outputs.length === 0 && (
        <div style={{ marginBottom: 6, fontFamily: MONO, fontSize: 9, color: gradientFrom, letterSpacing: 0.5, fontStyle: "italic" }}>
          ✓ {selected.length} device{selected.length > 1 ? "s" : ""} already selected — refresh to change
        </div>
      )}
      <button onClick={loadDevices} disabled={loading} style={{
        width: "100%", padding: "9px 0", borderRadius: 6, border: "none",
        background: loading ? `linear-gradient(90deg, ${gradientFrom}88, ${gradientTo}88)` : `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
        color: "#e8f4ff", fontFamily: MONO, fontWeight: 700, fontSize: 9,
        letterSpacing: 1.5, textTransform: "uppercase",
        cursor: loading ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        boxShadow: `0 3px 12px ${gradientFrom}33`, transition: "all 0.35s ease",
      }}>
        {loading && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#e8f4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ animation: "spin 1s linear infinite" }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
        )}
        {loading ? "Loading…" : loaded ? "🔄 Refresh Devices" : "🔍 Load Audio Devices"}
      </button>

      {outputs.length > 0 && (
        <div style={{ marginTop: 8, maxHeight: 110, overflowY: "auto",
          scrollbarWidth: "thin", scrollbarColor: "rgba(56,181,181,0.2) transparent" }}>
          {outputs.map(d => {
            const isChecked = selected.includes(d.deviceId);
            return (
              <label key={d.deviceId} onClick={() => toggle(d.deviceId)} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                borderRadius: 5, marginBottom: 4, cursor: "pointer",
                background: isChecked ? `${gradientFrom}18` : "rgba(255,255,255,0.02)",
                border: `1px solid ${isChecked ? gradientFrom + "55" : "rgba(255,255,255,0.06)"}`,
                transition: "all 0.2s",
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                  border: `1.5px solid ${isChecked ? gradientFrom : "rgba(255,255,255,0.2)"}`,
                  background: isChecked ? `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s",
                }}>
                  {isChecked && (
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#e8f4ff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                <span style={{ fontFamily: MONO, fontSize: 9, color: isChecked ? "#e8f4ff" : "#7aa8cc",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color 0.2s" }}>
                  {d.label || `Device ${d.deviceId.slice(0, 8)}`}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {selected.length > 0 && outputs.length > 0 && (
        <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 9, color: gradientFrom, letterSpacing: 0.5, fontStyle: "italic" }}>
          ✓ {selected.length} device{selected.length > 1 ? "s" : ""} selected
        </div>
      )}
    </div>
  );
};

// ── AffirmationTimerPanel (Step 4 substep B) ──────────────────────────────────
// Fully continuous: any whole second from 0 (off) up to 300 (5 min) is valid —
// e.g. 11s, 47s, 130s. The labelled marks below are just quick-jump reference
// points, not the only allowed values.
const MAX_INTERVAL_SECS = 300; // 5 min

const INTERVAL_MARKS = [
  { label: "5s",    secs: 5   },
  { label: "30s",   secs: 30  },
  { label: "1m",    secs: 60  },
  { label: "2m",    secs: 120 },
  { label: "5m",    secs: 300 },
];

function formatIntervalLabel(value: number): string {
  if (value === 0) return "Affirmations Off";
  if (value < 60) return `Every ${value}s`;
  const m = Math.floor(value / 60);
  const s = value % 60;
  return s === 0 ? `Every ${m} min` : `Every ${m}m ${s}s`;
}

interface AffirmationTimerPanelProps {
  gradientFrom: string;
  gradientTo:   string;
  value:        number;          // seconds, 0 = off
  onChange:     (secs: number) => void;
  onConfigured: () => void;      // called once a choice is made
}

const AffirmationTimerPanel: React.FC<AffirmationTimerPanelProps> = ({
  gradientFrom, gradientTo, value, onChange, onConfigured,
}) => {
  // Track whether the user has ever interacted (any selection counts, including "Off")
  const configuredRef = useRef(false);

  const handleSelect = (secs: number) => {
    onChange(Math.max(0, Math.min(MAX_INTERVAL_SECS, Math.round(secs))));
    if (!configuredRef.current) {
      configuredRef.current = true;
      onConfigured();
    }
  };

  // If a parent preset value already exists on mount, treat as configured
  useEffect(() => {
    // value !== -1 means it was set externally — mark configured immediately
    if (!configuredRef.current) {
      configuredRef.current = true;
      onConfigured();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayLabel = formatIntervalLabel(value);

  // Shared horizontal inset so the slider track and the marks row below it
  // occupy the EXACT same pixel width — this keeps every tick mark aligned
  // with its true position on the track, at any value, at any width.
  const TRACK_INSET = 4;

  return (
    <div style={{ marginTop: 8 }}>

      {/* ── Value readout ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 8,
      }}>
        <span style={{ fontFamily: MONO, fontSize: 8, color: `${gradientFrom}cc`, letterSpacing: 1 }}>
          INTERVAL
        </span>
        <span style={{
          fontFamily: MONO, fontSize: 9, fontWeight: 800,
          color: value === 0 ? "#4a6a8a" : gradientFrom,
          background: "#07101e",
          border: `1px solid ${value === 0 ? "#1e293b" : gradientFrom + "44"}`,
          borderRadius: 4, padding: "1px 7px",
          transition: "color 0.3s, border-color 0.3s",
        }}>
          {displayLabel}
        </span>
      </div>

      {/* ── Continuous drag slider — any second value from 0 to 5 min ── */}
      <div style={{ padding: `0 ${TRACK_INSET}px` }}>
        <SingleRangeSlider
          min={0} max={MAX_INTERVAL_SECS} step={1}
          value={value}
          onChange={handleSelect}
          color={gradientFrom}
        />
      </div>

      {/* ── Reference marks under the slider — positioned at their real spot
           on the 0–300s scale (not evenly spaced), tap to jump exactly there.
           Uses the SAME horizontal inset as the slider above so each mark's
           percentage position lines up with the actual thumb position. ── */}
      <div style={{ position: "relative", height: 12, marginTop: 5, padding: `0 ${TRACK_INSET}px` }}>
        {INTERVAL_MARKS.map((m) => {
          const pct = (m.secs / MAX_INTERVAL_SECS) * 100;
          const isSelected = value === m.secs;
          return (
            <span
              key={m.secs}
              onClick={() => handleSelect(m.secs)}
              style={{
                position: "absolute", left: `${pct}%`,
                transform: pct === 0 ? "translateX(0%)" : pct === 100 ? "translateX(-100%)" : "translateX(-50%)",
                fontFamily: MONO, fontSize: 7, letterSpacing: 0.3,
                color: isSelected ? gradientFrom : "#4a6a8a",
                fontWeight: isSelected ? 800 : 400,
                cursor: "pointer", transition: "color 0.18s",
                whiteSpace: "nowrap",
              }}
            >
              {m.label}
            </span>
          );
        })}
      </div>

      {/* ── Info hint ── */}
      <div style={{
        marginTop: 8,
        padding: "5px 8px",
        borderRadius: 5,
        background: value === 0 ? "rgba(74,106,138,0.07)" : `${gradientFrom}0c`,
        border: `1px solid ${value === 0 ? "rgba(74,106,138,0.15)" : gradientFrom + "22"}`,
        fontFamily: MONO,
        fontSize: 8,
        color: value === 0 ? "#4a6a8a" : `${gradientFrom}cc`,
        lineHeight: 1.5,
        transition: "all 0.3s",
      }}>
        {value === 0
          ? "Affirmations disabled — audio cues will not play during the session."
          : `An affirmation will play every ${displayLabel.replace("Every ", "")} during the session.`}
      </div>

    </div>
  );
};

// ── InfoTooltip ───────────────────────────────────────────────────────────────
// Small "i" badge — click to reveal an explanatory popover. Click again / click
// outside / press the badge again to close. Used next to every slider so users
// can learn what a control does without cluttering the UI by default.
//
// NOTE: the popover is rendered with `position: fixed`, anchored to the
// button's on-screen coordinates (via getBoundingClientRect). This is what
// keeps it from being clipped/hidden — several ancestors up the tree
// (step cards, the scroll area) use `overflow: hidden` / `overflow-y: auto`,
// which silently clips any `position: absolute` popover that tries to
// escape those bounds. `position: fixed` is positioned relative to the
// viewport instead, so it renders on top of everything regardless of any
// ancestor's overflow setting.
interface InfoTooltipProps {
  text: string;
  color?: string;
  align?: "left" | "right";
}

const TOOLTIP_WIDTH = 190;

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, color = "#38bdf8", align = "left" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      let left = align === "right" ? rect.right - TOOLTIP_WIDTH : rect.left;
      // Clamp so the popover never runs off either edge of the viewport
      left = Math.max(6, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - 6));
      setCoords({ top: rect.bottom + 4, left });
    }
    setOpen(v => !v);
  };

  // Keep the popover glued to the button on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      let left = align === "right" ? rect.right - TOOLTIP_WIDTH : rect.left;
      left = Math.max(6, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - 6));
      setCoords({ top: rect.bottom + 4, left });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, align]);

  return (
    <span style={{ position: "relative", display: "inline-flex", marginLeft: 5, flexShrink: 0 }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        aria-label="More info"
        style={{
          width: 13, height: 13, borderRadius: "50%",
          border: `1px solid ${color}66`,
          background: open ? `${color}33` : `${color}14`,
          color, fontFamily: MONO, fontSize: 8, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", padding: 0, lineHeight: 1,
          transition: "background 0.15s",
        }}
      >
        i
      </button>

      {open && coords && (
        <>
          {/* click-away catcher */}
          <div
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 9998 }}
          />
          <div
  onClick={(e) => e.stopPropagation()}
 style={{
  position: "fixed", top: coords.top, left: coords.left, zIndex: 9999,
  width: TOOLTIP_WIDTH, padding: "8px 10px", borderRadius: 6,
  background: "#0a1525", border: `1px solid ${color}44`,
  boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
  fontFamily: MONO, fontSize: 8, color: "#b9d2e8",
  lineHeight: 1.6, fontWeight: 400,
  whiteSpace: "pre-line",
}}
>
  {text}
</div>
        </>
      )}
    </span>
  );
};

// ── Dual Range Slider (single track, two handles — min left / max right) ──────
// ── Dual Range Slider — click/drag ANYWHERE on the track; moves whichever
// handle (min or max) is closer to where you clicked ─────────────────────────
interface DualRangeSliderProps {
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChangeMin: (v: number) => void;
  onChangeMax: (v: number) => void;
  color?: string;
  gap?: number;
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min, max, valueMin, valueMax, onChangeMin, onChangeMax,
  color = "#38bdf8", gap = 1,
}) => {
  const pctMin = ((valueMin - min) / (max - min)) * 100;
  const pctMax = ((valueMax - min) / (max - min)) * 100;
  const midpoint = (min + max) / 2;
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<"min" | "max" | null>(null);

  const valueFromClientX = (clientX: number) => {
    if (!trackRef.current) return min;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return min + ratio * (max - min);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const clicked = valueFromClientX(e.clientX);
    // move whichever handle is closer to the click point
    const nearer = Math.abs(clicked - valueMin) <= Math.abs(clicked - valueMax) ? "min" : "max";
    draggingRef.current = nearer;
    if (nearer === "min") onChangeMin(Math.min(clicked, valueMax - gap));
    else onChangeMax(Math.max(clicked, valueMin + gap));
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (e.buttons !== 1 || !draggingRef.current) return;
    const v = valueFromClientX(e.clientX);
    if (draggingRef.current === "min") onChangeMin(Math.min(v, valueMax - gap));
    else onChangeMax(Math.max(v, valueMin + gap));
  };

  const handlePointerUp = () => { draggingRef.current = null; };

  return (
    <div
      ref={trackRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{ position: "relative", height: 20, marginTop: 2, cursor: "pointer", touchAction: "none" }}
    >
      <div style={{
        position: "absolute", top: 8, left: 0, right: 0, height: 4,
        borderRadius: 4, background: "#1e293b",
      }} />
      <div style={{
        position: "absolute", top: 8, height: 4, borderRadius: 4,
        left: `${pctMin}%`, width: `${Math.max(0, pctMax - pctMin)}%`,
        background: `linear-gradient(90deg, ${color}aa, ${color})`,
        boxShadow: `0 0 6px ${color}66`,
        pointerEvents: "none",
      }} />
      <input
        type="range" min={min} max={max} value={valueMin}
        onChange={e => onChangeMin(Math.min(Number(e.target.value), valueMax - gap))}
        className="dual-range-thumb"
        style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: 20,
          margin: 0, background: "transparent", zIndex: valueMin > midpoint ? 5 : 3,
        }}
      />
      <input
        type="range" min={min} max={max} value={valueMax}
        onChange={e => onChangeMax(Math.max(Number(e.target.value), valueMin + gap))}
        className="dual-range-thumb"
        style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: 20,
          margin: 0, background: "transparent", zIndex: valueMin > midpoint ? 3 : 5,
        }}
      />
    </div>
  );
};

// ── Single Range Slider (thin track, fill-div driven by the same value used
// for the number readout — thumb position and displayed number can never
// drift apart, unlike a raw <input type="range"> with a CSS gradient hack) ───
// ── Single Range Slider — click/drag ANYWHERE on the track to set the value ───
interface SingleRangeSliderProps {
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
  color?: string;
}

const SingleRangeSlider: React.FC<SingleRangeSliderProps> = ({
  min, max, step = 1, value, onChange, color = "#3ecb8a",
}) => {
  const pct = ((value - min) / (max - min)) * 100;
  const trackRef = useRef<HTMLDivElement>(null);

  // Converts a pointer's clientX into a snapped value and fires onChange
  const setFromClientX = (clientX: number) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    let next = min + ratio * (max - min);
    next = Math.round(next / step) * step;
    next = Math.min(max, Math.max(min, next));
    onChange(next);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (e.buttons !== 1) return; // only while the button/finger is down
    setFromClientX(e.clientX);
  };

  return (
    <div
      ref={trackRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      style={{ position: "relative", height: 20, cursor: "pointer", touchAction: "none" }}
    >
      {/* base track */}
      <div style={{
        position: "absolute", top: 8, left: 0, right: 0, height: 4,
        borderRadius: 4, background: "#1e293b",
      }} />
      {/* fill — driven by the exact same `value` shown in the readout */}
      <div style={{
        position: "absolute", top: 8, left: 0, height: 4, borderRadius: 4,
        width: `${pct}%`,
        background: `linear-gradient(90deg, ${color}aa, ${color})`,
        boxShadow: `0 0 6px ${color}66`,
        pointerEvents: "none",
      }} />
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="single-range-thumb"
        style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: 20,
          margin: 0, background: "transparent",
        }}
      />
    </div>
  );
};
// ── AudioSettingsPanel (Step 4 substep C) ──────────────────────────────────────
// Houses ALL audio-related sliders in one place: sensitivity (how much eye
// movement is needed to reach full volume) + the min/max volume range.
// Each control has an info icon explaining what it does, and the Volume Range
// control has a play/stop preview button so the user can hear the affirmation
// clip at the exact volume their sliders are set to.
interface AudioSettingsPanelProps {
  gradientFrom: string;
  gradientTo:   string;
  audioSensitivity: number;            // 0–1000
  onAudioSensitivityChange: (v: number) => void;
  audioMinGain: number;                // 0.0 – 0.1
  audioMaxGain: number;                // 0.0 – 0.1
  onAudioMinGainChange: (v: number) => void;
  onAudioMaxGainChange: (v: number) => void;
  onConfigured: () => void;
}

const AudioSettingsPanel: React.FC<AudioSettingsPanelProps> = ({
  gradientFrom, gradientTo,
  audioSensitivity, onAudioSensitivityChange,
  audioMinGain, audioMaxGain, onAudioMinGainChange, onAudioMaxGainChange,
  onConfigured,
}) => {
  const configuredRef = useRef(false);
  useEffect(() => {
    if (!configuredRef.current) {
      configuredRef.current = true;
      onConfigured();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minPct  = Math.round(audioMinGain * 1000);
  const maxPct  = Math.round(audioMaxGain * 1000);

  // Same inset trick as the Affirmation Timer slider: the slider wrapper and
  // the marks row below it share this exact padding, so a mark's percentage
  // position always lines up with the true thumb position — previously the
  // marks row had no padding while the slider had "0 4px", which visually
  // shifted every mark a few pixels off from where the slider really was
  // (this is why e.g. "500" could appear to sit under the thumb while the
  // actual value was 300).
  const SENS_TRACK_INSET = 4;

  // ── Volume-range preview play/stop ──────────────────────────────────────
  // Plays the affirmation clip locally (no session required) at the volume
  // configured by the Min/Max Volume Range sliders below, so the user can
  // hear exactly what will play during a real session. Uses `audioMaxGain`
  // as the preview loudness (the "ceiling" of the configured range), and
  // updates live if the sliders are adjusted while it's playing.

// ── Volume-range preview — now TWO buttons: one previews at the min gain
// (left handle), one previews at the max gain (right handle). Both play the
// same stay-aware → stay-conscious chain; only the volume differs.
const [previewMode, setPreviewMode] = useState<"min" | "max" | null>(null);
const previewAudioRef  = useRef<HTMLAudioElement | null>(null);
const previewAudioRef2 = useRef<HTMLAudioElement | null>(null);

// Keep the live preview volume in sync with whichever slider is being
// previewed, so dragging while playing updates the sound immediately.
useEffect(() => {
  if (!previewMode) return;
  const vol = Math.min(1, Math.max(0, previewMode === "min" ? audioMinGain : audioMaxGain));
  if (previewAudioRef.current)  previewAudioRef.current.volume  = vol;
  if (previewAudioRef2.current) previewAudioRef2.current.volume = vol;
}, [audioMinGain, audioMaxGain, previewMode]);

const stopPreview = () => {
  previewAudioRef.current?.pause();
  if (previewAudioRef.current) previewAudioRef.current.currentTime = 0;
  previewAudioRef2.current?.pause();
  if (previewAudioRef2.current) previewAudioRef2.current.currentTime = 0;
  setPreviewMode(null);
};

const handleTogglePreview = (mode: "min" | "max") => {
  // Tapping the button that's already playing stops it.
  if (previewMode === mode) {
    stopPreview();
    return;
  }
  // Switching from the other button while it's playing — stop first.
  stopPreview();

  if (!previewAudioRef.current)  previewAudioRef.current  = new Audio(PREVIEW_AUDIO_SRC);
  if (!previewAudioRef2.current) previewAudioRef2.current = new Audio(PREVIEW_AUDIO_SRC_2);

  const audio  = previewAudioRef.current;
  const audio2 = previewAudioRef2.current;
  const vol = Math.min(1, Math.max(0, mode === "min" ? audioMinGain : audioMaxGain));

  audio.onended = () => {
    const liveVol = Math.min(1, Math.max(0, mode === "min" ? audioMinGain : audioMaxGain));
    audio2.currentTime = 0;
    audio2.volume = liveVol;
    audio2.play().catch(() => setPreviewMode(null));
  };
  audio2.onended = () => setPreviewMode(null);

  audio.volume = vol;
  audio.currentTime = 0;
  audio.play()
    .then(() => setPreviewMode(mode))
    .catch(() => setPreviewMode(null));
};

// Stop + release both preview clips on unmount
useEffect(() => {
  return () => {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    previewAudioRef2.current?.pause();
    previewAudioRef2.current = null;
  };
}, []);















const [previewPlaying, setPreviewPlaying] = useState(false);

// Keep the live preview volume in sync with the slider while playing —
// applies to whichever clip (stay-aware or stay-conscious) is active.
useEffect(() => {
  const vol = Math.min(1, Math.max(0, audioMaxGain));
  if (previewPlaying) {
    if (previewAudioRef.current)  previewAudioRef.current.volume  = vol;
    if (previewAudioRef2.current) previewAudioRef2.current.volume = vol;
  }
}, [audioMaxGain, previewPlaying]);


// Stop + release both preview clips on unmount
useEffect(() => {
  return () => {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    previewAudioRef2.current?.pause();
    previewAudioRef2.current = null;
  };
}, []);
  // Keep the live preview volume in sync with the slider while it's playing
  useEffect(() => {
    if (previewPlaying && previewAudioRef.current) {
      previewAudioRef.current.volume = Math.min(1, Math.max(0, audioMaxGain));
    }
  }, [audioMaxGain, previewPlaying]);


  // Stop + release the preview audio on unmount
  useEffect(() => {
    return () => {
      previewAudioRef.current?.pause();
      previewAudioRef.current = null;
    };
  }, []);

  return (
    <div style={{ marginTop: 8 }}>

      {/* ── Audio Sensitivity ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ display: "flex", alignItems: "center", fontFamily: MONO, fontSize: 8, color: `${gradientFrom}cc`, letterSpacing: 1, textTransform: "uppercase" }}>
            📈 Sensitivity
       <InfoTooltip
  color={gradientFrom}
  text={`Controls how much signal amplitude is needed to reach full affirmation volume.
- Lower = very sensitive, reaches max volume with small amplitude changes.
- Higher = needs a stronger amplitude swing before getting louder.`}
/>
          </span>
          <span style={{
            fontFamily: MONO, fontSize: 9, fontWeight: 800, color: gradientFrom,
            background: "#07101e", border: `1px solid ${gradientFrom}44`, borderRadius: 4, padding: "1px 7px",
          }}>
            {audioSensitivity}
          </span>
        </div>

        <div style={{ padding: `0 ${SENS_TRACK_INSET}px` }}>
          <SingleRangeSlider
            min={0} max={1000} step={5}
            value={audioSensitivity}
            onChange={onAudioSensitivityChange}
            color={gradientFrom}
          />
        </div>

        {/* marks row — position: absolute, percentage-aligned, same inset as slider above */}
        <div style={{ position: "relative", height: 12, marginTop: 5, padding: `0 ${SENS_TRACK_INSET}px` }}>
          {[0, 500, 1000].map(v => {
            const pct = (v / 1000) * 100;
            const isSelected = audioSensitivity === v;
            return (
              <span
                key={v}
                onClick={() => onAudioSensitivityChange(v)}
                style={{
                  position: "absolute", left: `${pct}%`,
                  transform: pct === 0 ? "translateX(0%)" : pct === 100 ? "translateX(-100%)" : "translateX(-50%)",
                  fontFamily: MONO, fontSize: 7, letterSpacing: 0.3,
                  color: isSelected ? gradientFrom : "#334155",
                  fontWeight: isSelected ? 800 : 400,
                  cursor: "pointer", transition: "color 0.18s",
                  whiteSpace: "nowrap",
                }}
              >
                {v}
              </span>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 3 }}>
          <span style={{ fontFamily: MONO, fontSize: 7, color: `${gradientFrom}88`, letterSpacing: 0.5 }}>
            {audioSensitivity <= 200 ? "very sensitive" : audioSensitivity <= 500 ? "default" : "less sensitive"}
          </span>
        </div>
      </div>

      <div style={{ height: 1, background: `${gradientFrom}1f`, marginBottom: 12 }} />

      {/* ── Volume Range (min / max in one slider) ── */}
{/* ── Volume Range (min / max in one slider) ── */}
<div>
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
    <span style={{ display: "flex", alignItems: "center", fontFamily: MONO, fontSize: 8, color: `${gradientFrom}cc`, letterSpacing: 1, textTransform: "uppercase" }}>
      🔊 Volume Range
   <InfoTooltip
  color={gradientFrom}
  text={`Sets the minimum and maximum levels for affirmation volume.
- Left handle = minimum volume (always audible, even with low signal amplitude).
- Right handle = maximum volume (played during high amplitude activity).`}
/>
    </span>

    <span style={{
      fontFamily: MONO, fontSize: 9, fontWeight: 800, color: gradientFrom,
      background: "#07101e", border: `1px solid ${gradientFrom}44`, borderRadius: 4, padding: "1px 7px",
    }}>
      {minPct / 10}% – {maxPct / 10}%
    </span>
  </div>

  {/* Slider flanked by two preview buttons — left previews MIN, right previews MAX */}
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <button
      onClick={() => handleTogglePreview("min")}
      title={previewMode === "min" ? "Stop preview" : `Preview at ${minPct / 10}% (minimum)`}
  aria-label={previewMode === "min" ? "Stop minimum-volume preview" : "Play minimum-volume preview"}
      style={{
        width: 22, height: 22, borderRadius: 6, padding: 0, flexShrink: 0,
        border: `1px solid ${previewMode === "min" ? "#ef444455" : gradientFrom + "55"}`,
        background: previewMode === "min" ? "#ef444414" : `${gradientFrom}14`,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s ease",
      }}
    >
      {previewMode === "min" ? (
        <svg width="8" height="8" viewBox="0 0 24 24" fill="#ef4444" stroke="none">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
        </svg>
      ) : (
        <svg width="8" height="8" viewBox="0 0 24 24" fill={gradientFrom} stroke="none">
          <polygon points="5,3 19,12 5,21"/>
        </svg>
      )}
    </button>

    <div style={{ flex: 1, padding: "0 6px" }}>
      <DualRangeSlider
        min={0} max={100}
        valueMin={minPct}
        valueMax={maxPct}
        onChangeMin={v => onAudioMinGainChange(v / 1000)}
        onChangeMax={v => onAudioMaxGainChange(v / 1000)}
        color={gradientFrom}
      />
    </div>

    <button
      onClick={() => handleTogglePreview("max")}
      title={previewMode === "max" ? "Stop preview" : `Preview at ${maxPct / 10}% (maximum)`}
  aria-label={previewMode === "max" ? "Stop maximum-volume preview" : "Play maximum-volume preview"}
 
      style={{
        width: 22, height: 22, borderRadius: 6, padding: 0, flexShrink: 0,
        border: `1px solid ${previewMode === "max" ? "#ef444455" : gradientFrom + "55"}`,
        background: previewMode === "max" ? "#ef444414" : `${gradientFrom}14`,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s ease",
      }}
    >
      {previewMode === "max" ? (
        <svg width="8" height="8" viewBox="0 0 24 24" fill="#ef4444" stroke="none">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
        </svg>
      ) : (
        <svg width="8" height="8" viewBox="0 0 24 24" fill={gradientFrom} stroke="none">
          <polygon points="5,3 19,12 5,21"/>
        </svg>
      )}
    </button>
  </div>
<div style={{ display: "flex", justifyContent: "space-between", padding: "3px 6px 0", fontFamily: MONO, fontSize: 7, color: "#334155" }}>
  <span>0%</span>
  <span style={{ color: `${gradientFrom}55` }}>minimum → maximum</span>
  <span>10%</span>
</div>

<div style={{ display: "flex", gap: 6, marginTop: 8 }}>
  <div style={{ flex: 1, fontFamily: MONO, fontSize: 7, color: `${gradientFrom}77`, lineHeight: 1.5 }}>
    🔈 <b style={{ color: `${gradientFrom}cc` }}>{minPct / 10}%</b> minimum
  </div>
  <div style={{ flex: 1, fontFamily: MONO, fontSize: 7, color: `${gradientFrom}77`, lineHeight: 1.5, textAlign: "right" }}>
    🔊 <b style={{ color: `${gradientFrom}cc` }}>{maxPct / 10}%</b> maximum
  </div>
</div>
  {/* preview status hint */}
 <div style={{
  marginTop: 8, padding: "5px 8px", borderRadius: 5,
  background: previewMode ? "#ef444414" : `${gradientFrom}0c`,
  border: `1px solid ${previewMode ? "#ef444433" : gradientFrom + "22"}`,
  fontFamily: MONO, fontSize: 7,
  color: previewMode ? "#ef4444" : `${gradientFrom}aa`,
  lineHeight: 1.5, transition: "all 0.3s",
}}>
  {previewMode === "min"
    ? `▶ Playing preview at ${minPct / 10}% (minimum) — tap ⏹ to stop.`
    : previewMode === "max"
    ? `▶ Playing preview at ${maxPct / 10}% (maximum) — tap ⏹ to stop.`
    : `Tap ▶ on either side to preview at minimum (${minPct / 10}%) or maximum (${maxPct / 10}%) volume.`}
</div>
</div>

    </div>
  );
};

// ── Step definitions ──────────────────────────────────────────────────────────
type SubstepType = "sensor-connect" | "speaker-panel" | "signal-check" | "affirmation-timer" | "audio-settings";

interface Substep {
  type: SubstepType; label: string;
}

interface StepDef {
  number: number; title: string; tag: string;
  from: string; to: string;
  control: Substep;
  extra?: Substep;
  icon: (color: string) => React.ReactElement;
}

const STEPS: StepDef[] = [
  {
    number: 1, title: "Dream Sensor", tag: "SENSOR",
    from: STEP_PALETTE[0].from, to: STEP_PALETTE[0].to,
    control: { type: "sensor-connect", label: "Connect sensor to device" },
    icon: (c) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"/>
        <circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"/>
        <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/>
      </svg>
    ),
  },
  {
    number: 2, title: "Audio Headset", tag: "AUDIO",
    from: STEP_PALETTE[1].from, to: STEP_PALETTE[1].to,
    control: { type: "speaker-panel", label: "Select output device" },
    icon: (c) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
        <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
      </svg>
    ),
  },
  {
    number: 3, title: "Gel Electrodes", tag: "SIGNAL",
    from: STEP_PALETTE[2].from, to: STEP_PALETTE[2].to,
    control: { type: "signal-check", label: "Verify live signal" },
    icon: (c) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>
        <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
        <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
      </svg>
    ),
  },
  {
    number: 4, title: "Audio & Timing", tag: "TIMER",
    from: STEP_PALETTE[3].from, to: STEP_PALETTE[3].to,
    control: { type: "affirmation-timer", label: "Set playback interval" },
    extra:   { type: "audio-settings",    label: "Audio Settings" },
    icon: (c) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ collapsed, color = "#7aa8cc" }: { collapsed: boolean; color?: string }) {
  return (
    <svg
      width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 0.25s", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", flexShrink: 0 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ── Toggle Switch ──────────────────────────────────────────────────────────────
interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (v: boolean) => void;
  color?: string;
  size?: "sm" | "xs" | "xxs";
}

function ToggleSwitch({ enabled, onChange, color = "#c8a050", size = "sm" }: ToggleSwitchProps) {
  const w = size === "xxs" ? 20 : size === "xs" ? 26 : 30;
  const h = size === "xxs" ? 11 : size === "xs" ? 14 : 16;
  const r = size === "xxs" ? 3.5 : size === "xs" ? 5 : 6;

  return (
    <div
      onClick={() => onChange(!enabled)}
      style={{
        width: w, height: h, borderRadius: h / 2,
        background: enabled ? color : "#1e293b",
        border: `1px solid ${enabled ? color : "#334155"}`,
        cursor: "pointer", position: "relative",
        transition: "all 0.2s ease", flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute",
        top: "50%", transform: "translateY(-50%)",
        left: enabled ? `calc(100% - ${r * 2 + 2}px)` : 2,
        width: r * 2, height: r * 2,
        borderRadius: "50%",
        background: enabled ? "#040f18" : "#475569",
        transition: "left 0.2s ease",
        boxShadow: enabled ? `0 0 4px ${color}88` : "none",
      }} />
    </div>
  );
}

// ── Admin Panel ───────────────────────────────────────────────────────────────
// NOTE: As of this revision, AdminPanel is STATUS-ONLY. All audio sliders
// (sensitivity, min/max volume) now live in Step 4 → "Audio Settings".
// AdminPanel just shows live state (data vs override, REM/audio status)
// and exposes the eye-state override + play/pause/stop controls.
interface AdminPanelProps {
  adminEyeOverride: EyeStateType | null;
  onAdminEyeOverrideChange: (state: EyeStateType | null) => void;
  dreamingActive: boolean;
  dreamAudioPaused: boolean;
  onPlayAffirmationNow: () => void;
  onStopAffirmation: () => void;
  dataEyeState: EyeStateType;
  sessionActive: boolean;
  remDebugEnabled: boolean;
  onRemDebugToggle: (v: boolean) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({
  adminEyeOverride,
  onAdminEyeOverrideChange,
  dreamingActive,
  dreamAudioPaused,
  onPlayAffirmationNow,
  onStopAffirmation,
  dataEyeState,
  sessionActive,
  remDebugEnabled,
  onRemDebugToggle,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      style={{
        borderTop:      "1px solid rgba(200,160,80,0.25)",
        background:     "rgba(4,10,22,0.97)",
        backdropFilter: "blur(12px)",
        flexShrink:     0,
      }}
    >
      <div
        onClick={() => setCollapsed(v => !v)}
        style={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "6px 12px",
          cursor:         "pointer",
          userSelect:     "none",
          borderBottom:   collapsed ? "none" : "1px solid rgba(200,160,80,0.12)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 10 }}>🔧</span>
          <span style={{ fontFamily: MONO, fontSize: 8, fontWeight: 800, letterSpacing: 1.4, color: "#c8a05099", textTransform: "uppercase" }}>
            Admin
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: adminEyeOverride ? "#c8a05014" : "#0f1e30", border: `1px solid ${adminEyeOverride ? "#c8a05044" : "#1e3050"}`, borderRadius: 20, padding: "1px 7px" }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: adminEyeOverride ? "#c8a050" : "#38bdf8", flexShrink: 0, display: "inline-block" }} />
            <span style={{ fontFamily: MONO, fontSize: 7, color: adminEyeOverride ? "#c8a050" : "#38bdf8" }}>
              {adminEyeOverride ? "override" : "data"}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {dreamingActive && !collapsed && (
            <>
              {dreamAudioPaused ? (
                <button
                  onClick={e => { e.stopPropagation(); onPlayAffirmationNow(); }}
                  title="Resume dream audio"
                  style={{
                    width: 22, height: 22, borderRadius: 5, padding: 0,
                    border: "1px solid #4ade8055", background: "#4ade8018",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="#4ade80" stroke="none">
                    <polygon points="5,3 19,12 5,21"/>
                  </svg>
                </button>
              ) : (
                <>
                  <button
                    onClick={e => { e.stopPropagation(); onPlayAffirmationNow(); }}
                    title="Play affirmation now"
                    style={{
                      width: 22, height: 22, borderRadius: 5, padding: 0,
                      border: "1px solid #c8a05055", background: "#c8a05018",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="#c8a050" stroke="none">
                      <polygon points="5,3 19,12 5,21"/>
                    </svg>
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); onStopAffirmation(); }}
                    title="Stop dream audio"
                    style={{
                      width: 22, height: 22, borderRadius: 5, padding: 0,
                      border: "1px solid #ef444455", background: "#ef444414",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                  >
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="#ef4444" stroke="none">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                    </svg>
                  </button>
                </>
              )}
            </>
          )}

          {adminEyeOverride !== null && !collapsed && (
            <button
              onClick={e => { e.stopPropagation(); onAdminEyeOverrideChange(null); }}
              title="Follow sensor data"
              style={{
                padding: "2px 8px", borderRadius: 5,
                border: "1px solid #38bdf833", background: "#38bdf811",
                color: "#38bdf8", fontFamily: MONO, fontSize: 8, fontWeight: 700,
                cursor: "pointer", letterSpacing: 0.5,
              }}
            >
              ↩ Data
            </button>
          )}
          <ChevronIcon collapsed={collapsed} color="#c8a05066" />
        </div>
      </div>

      {!collapsed && (
      <div style={{ padding: "8px 12px 10px" }}>

          {dreamingActive && (
            <div style={{
              marginBottom: 8, padding: "5px 8px", borderRadius: 6,
              background: dreamAudioPaused ? "rgba(239,68,68,0.08)" : "rgba(200,160,80,0.08)",
              border: `1px solid ${dreamAudioPaused ? "#ef444433" : "#c8a05033"}`,
              display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            }}>
              <span style={{ fontFamily: MONO, fontSize: 8, color: dreamAudioPaused ? "#ef4444" : "#c8a050", fontWeight: 700, letterSpacing: 0.5 }}>
                {dreamAudioPaused ? "⏸ Audio paused" : "▶ Both audios playing"}
              </span>
              {!dreamAudioPaused && (
                <span style={{ fontFamily: MONO, fontSize: 7, color: "#c8a05066" }}>
                  (background loop + affirmation schedule)
                </span>
              )}
            </div>
          )}

          {!dreamingActive && (
            <div style={{
              marginBottom: 8, padding: "5px 8px", borderRadius: 6,
              background: "rgba(56,189,248,0.06)",
              border: "1px solid rgba(56,189,248,0.18)",
              display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            }}>
              <span style={{ fontFamily: MONO, fontSize: 8, color: "#38bdf8", fontWeight: 700, letterSpacing: 0.5 }}>
                ● Session active
              </span>
              <span style={{ fontFamily: MONO, fontSize: 7, color: "#38bdf866" }}>
                waiting for REM state
              </span>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
            <span style={{ fontFamily: MONO, fontSize: 7, color: "#c8a05088", letterSpacing: 1, textTransform: "uppercase" }}>
              👁 Eye override
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#07101e", border: "1px solid #1e293b", borderRadius: 5, padding: "2px 6px" }}>
              <span style={{ fontFamily: MONO, fontSize: 7, color: "#475569" }}>data →</span>
              {(() => {
                const cfg = EYE_STATES.find(s => s.value === dataEyeState)!;
                return (
                  <span style={{ fontFamily: MONO, fontSize: 8, fontWeight: 700, color: cfg.color }}>
                    {cfg.emoji} {cfg.label}
                  </span>
                );
              })()}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
            {EYE_STATES.map((state) => {
              const isSelected  = adminEyeOverride === state.value;
              const isDataState = dataEyeState === state.value && adminEyeOverride === null;

              return (
                <button
                  key={state.value}
                  onClick={() => isSelected ? onAdminEyeOverrideChange(null) : onAdminEyeOverrideChange(state.value)}
                  title={state.desc}
                  style={{
                    padding: "5px 3px", borderRadius: 6,
                    border: isSelected ? `1.5px solid ${state.color}88` : isDataState ? `1px solid ${state.color}44` : "1px solid #1e293b",
                    background: isSelected ? `${state.color}1a` : isDataState ? `${state.color}08` : "rgba(255,255,255,0.02)",
                    cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    transition: "all 0.18s", boxShadow: isSelected ? `0 2px 8px ${state.color}22` : "none",
                    position: "relative",
                  }}
                >
                  {isDataState && !isSelected && (
                    <span style={{ position: "absolute", top: 3, right: 3, width: 4, height: 4, borderRadius: "50%", background: state.color, display: "inline-block", boxShadow: `0 0 4px ${state.color}` }} />
                  )}
                  {isSelected && (
                    <span style={{ position: "absolute", top: 2, right: 4, fontFamily: MONO, fontSize: 6, color: state.color, fontWeight: 800 }}>✓</span>
                  )}
                  <span style={{ fontSize: 14, lineHeight: 1 }}>{state.emoji}</span>
                  <span style={{ fontFamily: MONO, fontSize: 7, fontWeight: 700, letterSpacing: 0.3, color: isSelected ? state.color : isDataState ? `${state.color}cc` : "#475569", transition: "color 0.18s", textAlign: "center" }}>
                    {state.label}
                  </span>
                </button>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
};

// ── Main SetupPane ────────────────────────────────────────────────────────────
const SetupPane: React.FC<SetupPaneProps> = ({
  onHzChange, onConnectionChange, toast,
  selectedDeviceIds, onDeviceSelectionChange, onBeginSession, onEndSession, sessionActive, showPractice,
  affirmationInterval = 60,
  onAffirmationIntervalChange,
  isAdmin = false,
  practiceCompleted = false,
  autoAffirmation = true,
  onAutoAffirmationChange,
  remThreshold = 250,
  onRemThresholdChange,
  adminEyeOverride = null,
  onAdminEyeOverrideChange,
  dreamingActive = false,
  dreamAudioPaused = false,
  onPlayAffirmationNow,
  onStopAffirmation,
  dataEyeState = "open",
  remDebugEnabled = false,
  onRemDebugToggle,
    audioMinGain = 0.015,
  audioMaxGain = 0.05,
  onAudioMinGainChange,
  onAudioMaxGainChange,
  bothConnected = false,
  advancedView = false,
  onToggleAdvanced,
   onManualDisconnectStart,
}) => {
  const [activeStep,      setActiveStep]      = useState<number>(1);
  const [completedSteps,  setCompletedSteps]  = useState<Set<number>>(new Set());
  const [collapsedSteps,  setCollapsedSteps]  = useState<Set<number>>(new Set());
  const DEV_MODE = false;
  const [isConnected,   setIsConnected]   = useState(false);
  const [liveAmplitude, setLiveAmplitude] = useState({ ch1: 0, ch2: 0, ch3: 0 });

  // Local affirmation interval — kept in sync with prop
  const [localInterval, setLocalInterval] = useState<number>(affirmationInterval);
  useEffect(() => { setLocalInterval(affirmationInterval); }, [affirmationInterval]);

  const fontLinkRef = useRef<HTMLLinkElement | null>(null);

  const current    = activeStep > 0 ? STEPS[activeStep - 1] : undefined;
  const isLastStep = activeStep === STEPS.length;
  const allDone    = activeStep === 0;

  const showAdminPanel = isAdmin && practiceCompleted && sessionActive;
  // Advanced-view toggle — only relevant once both devices are connected,
  // and hidden entirely while the practice flow controls the panel.
 // setup.tsx
const showAdvancedToggle = (bothConnected || sessionActive) && !!onToggleAdvanced;

  const isComplete     = (n: number) => completedSteps.has(n);
  const markComplete   = (n: number) => setCompletedSteps(prev => new Set([...Array.from(prev), n]));
  const unmarkComplete = (n: number) => setCompletedSteps(prev => {
    const next = new Set(Array.from(prev));
    next.delete(n);
    return next;
  });

  const isStepCollapsed    = (n: number) => collapsedSteps.has(n);
  const toggleStepCollapse = (n: number) => {
    setCollapsedSteps(prev => {
      const next = new Set(Array.from(prev));
      if (next.has(n)) { next.delete(n); } else { next.add(n); }
      return next;
    });
  };

  const canNavigateTo = (target: number) => {
    if (target <= activeStep) return true;
    for (let i = 1; i < target; i++) { if (!completedSteps.has(i)) return false; }
    return true;
  };

  const handleNavClick = (n: number) => {
    if (!canNavigateTo(n)) return;
    setActiveStep(n);
    setCollapsedSteps(prev => {
      const next = new Set(Array.from(prev));
      [1, 2, 3, 4].forEach(s => { if (s !== n) next.add(s); else next.delete(s); });
      return next;
    });
  };

  const handleBack = () => {
    const prev = Math.max(1, activeStep - 1);
    setActiveStep(prev);
    setCollapsedSteps(p => { const n = new Set(Array.from(p)); n.delete(prev); return n; });
  };

  const handleNext = () => {
    if (!isLastStep && !isComplete(activeStep)) return;
    if (isLastStep) {
      markComplete(activeStep);
      setActiveStep(0);
      setCollapsedSteps(p => {
        const n = new Set(Array.from(p));
        n.add(activeStep);
        return n;
      });
    } else {
      const curr = activeStep;
      const next = activeStep + 1;
      setActiveStep(next);
      setCollapsedSteps(p => {
        const n = new Set(Array.from(p));
        n.add(curr);
        n.delete(next);
        return n;
      });
    }
  };

  const nextDisabled = isLastStep ? false : !isComplete(activeStep);

  const handleHzChange = (data: HzData) => {
    if (typeof data === "object") setLiveAmplitude(data);
    onHzChange(data);
  };

  const handleConnectionChange = (status: boolean) => {
    setIsConnected(status);
    if (!status) {
      setLiveAmplitude({ ch1: 0, ch2: 0, ch3: 0 });
    } else {
      if (selectedDeviceIds.length > 0) { markComplete(2); }
    }
    onConnectionChange(status);
  };

  const handleSensorDisconnect = () => {
    setIsConnected(false);
    setLiveAmplitude({ ch1: 0, ch2: 0, ch3: 0 });
    onConnectionChange(false);
    onHzChange(0);
    unmarkComplete(1);
    unmarkComplete(2);
    unmarkComplete(3);
  };

 const handleEndSession = () => {
  onManualDisconnectStart?.();    // ← mark intentional FIRST, before anything else changes
  handleSensorDisconnect();
  onDeviceSelectionChange([]);
  setCompletedSteps(new Set());
  setCollapsedSteps(new Set());
  setActiveStep(1);
  onEndSession?.();
};
  useEffect(() => {
    const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
    if (!existing) {
      const link = document.createElement("link");
      link.rel  = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(link);
      fontLinkRef.current = link;
    }
    return () => {
      if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
        document.head.removeChild(fontLinkRef.current);
        fontLinkRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!DEV_MODE) return;
    setCompletedSteps(new Set([1, 2, 3, 4]));
    setActiveStep(0);
  }, []);

  // ── Render substep control ────────────────────────────────────────────
  const renderSubstepControl = (sub: Substep, step: StepDef) => {
    if (sub.type === "sensor-connect") {
      return (
        <SensorConnectButton
          gradientFrom={step.from} gradientTo={step.to}
          onHzChange={handleHzChange}
          onConnectionChange={handleConnectionChange}
          toast={toast}
          onConnected={() => markComplete(step.number)}
          isConnected={isConnected}
          onDisconnect={handleSensorDisconnect}
           onEndSession={handleEndSession}
           onManualDisconnectStart={onManualDisconnectStart}
        />
      );
    }
    if (sub.type === "speaker-panel") {
      return (
        <SpeakerPanel
          gradientFrom={step.from} gradientTo={step.to}
          selected={selectedDeviceIds} onChange={onDeviceSelectionChange}
          toast={toast}
          onConnected={() => markComplete(step.number)}
        />
      );
    }
    if (sub.type === "signal-check") {
      return (
        <SignalCheckPanel
          connected={isConnected}
          amplitude={liveAmplitude}
          accentFrom={step.from}
          accentTo={step.to}
          onSignalValid={() => markComplete(step.number)}
          onSignalInvalid={() => unmarkComplete(step.number)}
        />
      );
    }
    if (sub.type === "affirmation-timer") {
      return (
        <AffirmationTimerPanel
          gradientFrom={step.from}
          gradientTo={step.to}
          value={localInterval}
          onChange={(secs) => {
            setLocalInterval(secs);
            onAffirmationIntervalChange?.(secs);
          }}
          onConfigured={() => markComplete(step.number)}
        />
      );
    }
    if (sub.type === "audio-settings") {
      return (
        <AudioSettingsPanel
          gradientFrom={step.from}
          gradientTo={step.to}
          audioSensitivity={remThreshold}
          onAudioSensitivityChange={(v) => onRemThresholdChange?.(v)}
          audioMinGain={audioMinGain}
          audioMaxGain={audioMaxGain}
          onAudioMinGainChange={(v) => onAudioMinGainChange?.(v)}
          onAudioMaxGainChange={(v) => onAudioMaxGainChange?.(v)}
          onConfigured={() => markComplete(step.number)}
        />
      );
    }
    return null;
  };

  return (
    <>
      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes adminSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(56,181,181,0.20); border-radius: 99px; }
        .admin-rem-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 12px; height: 12px;
          border-radius: 50%;
          background: #c8a050;
          cursor: pointer;
          border: 2px solid #040f18;
          box-shadow: 0 0 5px rgba(200,160,80,0.45);
        }
        .admin-rem-slider::-moz-range-thumb {
          width: 12px; height: 12px;
          border-radius: 50%;
          background: #c8a050;
          cursor: pointer;
          border: 2px solid #040f18;
        }
        .single-range-thumb {
          pointer-events: none;
          -webkit-appearance: none;
          appearance: none;
        }
        .single-range-thumb::-webkit-slider-runnable-track {
          background: transparent;
        }
        .single-range-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: auto;
          width: 10px; height: 10px;
          border-radius: 50%;
          background: #3ecb8a;
          cursor: pointer;
          border: 2px solid #040f18;
          box-shadow: 0 0 4px rgba(62,203,138,0.5);
          margin-top: -3px;
        }
        .single-range-thumb::-moz-range-track {
          background: transparent;
        }
        .single-range-thumb::-moz-range-thumb {
          pointer-events: auto;
          width: 10px; height: 10px;
          border-radius: 50%;
          background: #3ecb8a;
          cursor: pointer;
          border: 2px solid #040f18;
        }
        .dual-range-thumb {
          pointer-events: none;
          -webkit-appearance: none;
          appearance: none;
        }
        .dual-range-thumb::-webkit-slider-runnable-track {
          background: transparent;
        }
        .dual-range-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: auto;
          width: 10px; height: 10px;
          border-radius: 50%;
          background: #3ecb8a;
          cursor: pointer;
          border: 2px solid #040f18;
          box-shadow: 0 0 4px rgba(62,203,138,0.5);
          margin-top: -3px;
        }
        .dual-range-thumb::-moz-range-track {
          background: transparent;
        }
        .dual-range-thumb::-moz-range-thumb {
          pointer-events: auto;
          width: 10px; height: 10px;
          border-radius: 50%;
          background: #3ecb8a;
          cursor: pointer;
          border: 2px solid #040f18;
        }
      `}</style>

      <aside style={S.sidebar}>

        {/* ── Header ── */}
        <div style={{ ...S.header, position: "relative" }}>
          <div style={S.headerTitle}>
           
          </div>

          {/* Advanced View toggle — centered in the header, independent of the L/R content */}
        {showAdvancedToggle && (
  <div style={{
    position: "absolute",
    left: "26%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex", alignItems: "center", gap: 7,
  }}>
    <span style={{
      fontFamily: MONO, fontSize: 11, letterSpacing: 1,
      textTransform: "uppercase",
      color: advancedView ? ACCENT : "#7aa8cc",
      transition: "color 0.3s", userSelect: "none",
    }}>
      Advanced View
    </span>
    <ToggleSwitch
      enabled={advancedView}
      onChange={(v) => onToggleAdvanced?.(v)}
      color={ACCENT}
      size="xs"
    />
  </div>
)}

         <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
 {!showPractice && (bothConnected || sessionActive) && (
    <button onClick={onBeginSession} style={{
      padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      border: "1px solid #38bdf844", background: "#38bdf811",
      color: "#38bdf8", cursor: "pointer", whiteSpace: "nowrap", fontFamily: MONO,
    }}>
      🧘 Practice
    </button>
  )}
</div>
        </div>

        {/* ── Subtitle ── */}
        <div style={S.subtitleBar}>
          Setup &nbsp;·&nbsp;{" "}
          {allDone
            ? <span style={{ color: ACCENT }}>All steps complete</span>
            : <span style={{ color: ACCENT }}>{current?.title}</span>
          }
        </div>

        {/* ── Step nav pills ── */}
        <div style={S.navRow}>
          {STEPS.map(s => {
            const isActive  = activeStep === s.number;
            const isDone    = isComplete(s.number);
            const isAllowed = canNavigateTo(s.number);
            const isLocked  = !isAllowed && !isDone;
            return (
              <button key={s.number} onClick={() => handleNavClick(s.number)}
                disabled={!isAllowed}
                title={!isAllowed ? "Complete the current step first" : undefined}
                style={{
                  flex: 1, padding: "5px 0", borderRadius: 5,
                  border: isDone ? `1px solid ${s.from}88` : isActive ? `1px solid ${s.from}55` : `1px solid ${s.from}44`,
                  background: isDone ? `linear-gradient(90deg, ${s.from}, ${s.to})` : isActive ? `${s.from}18` : `${s.from}10`,
                  color: isDone ? "#e8f4ff" : isActive ? s.from : `${s.from}66`,
                  fontFamily: MONO, fontWeight: isActive || isDone ? 700 : 400,
                  fontSize: 9, letterSpacing: 1,
                  cursor: isAllowed ? "pointer" : "not-allowed", transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                  boxShadow: isDone ? `0 2px 8px ${s.from}44` : "none",
                }}>
                <span>{s.number}</span>
                {isDone ? (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#e8f4ff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="none"
                    stroke={isLocked ? "#e0555599" : "#e05555"} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Step cards (scroll area) ── */}
        <div style={S.scrollArea}>
          {STEPS.map(step => {
            const isActive    = activeStep === step.number;
            const isDone      = isComplete(step.number);
            const isAllowed   = canNavigateTo(step.number);
            const isLocked    = !isAllowed && !isDone;
            const isCollapsed = isStepCollapsed(step.number);
            const substeps    = step.extra ? [step.control, step.extra] : [step.control];

            return (
              <div key={step.number}
                style={{
                  marginBottom: 8, borderRadius: 8,
                  border: (isActive && !isCollapsed) ? `1px solid ${step.from}44` : `1px solid ${step.from}33`,
                  background: (isActive && !isCollapsed) ? "rgba(255,255,255,0.03)" : `${step.from}0a`,
                  boxShadow: (isActive && !isCollapsed) ? `0 4px 18px ${step.from}14` : "none",
                  overflow: "hidden", transition: "all 0.25s ease",
                  opacity: isLocked ? 0.60 : 1,
                }}>

                <div
                  onClick={() => {
                    if (isLocked) return;
                    if (!isActive && isAllowed) {
                      handleNavClick(step.number);
                    } else {
                      toggleStepCollapse(step.number);
                    }
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "9px 11px",
                    background: (isActive && !isCollapsed) ? `${step.from}10` : "transparent",
                    borderBottom: (isActive && !isCollapsed) ? `1px solid ${step.from}20` : "1px solid transparent",
                    cursor: isLocked ? "not-allowed" : "pointer",
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: isActive ? `linear-gradient(135deg, ${step.from}, ${step.to})` : isDone ? `${step.from}28` : `${step.from}18`,
                    border: isActive ? "none" : isDone ? `1px solid ${step.from}60` : `1px solid ${step.from}44`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    boxShadow: isActive ? `0 2px 8px ${step.from}44` : "none", transition: "all 0.25s",
                  }}>
                    <span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 10,
                      color: isActive ? "#e8f4ff" : isDone ? step.from : `${step.from}88` }}>
                      {step.number}
                    </span>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: MONO, fontSize: 8, fontWeight: 700, letterSpacing: 2,
                      color: isActive ? step.from : isDone ? `${step.from}70` : `${step.from}66`, transition: "color 0.25s" }}>
                      {step.tag}
                    </div>
                    <div style={{
                      fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700,
                      fontSize: "clamp(10px,0.9vw,12px)",
                      color: isActive ? "#e8f4ff" : isDone ? "#7aa8cc" : "#5a7a96",
                      transition: "color 0.25s", marginTop: 1,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {step.title}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <div style={{ opacity: isActive ? 1 : isDone ? 0.55 : 0.45, transition: "opacity 0.2s" }}>
                      {step.icon(step.from)}
                    </div>
                    {(isActive || isDone) && (
                      <ChevronIcon collapsed={isCollapsed} color={isActive ? `${step.from}cc` : `${step.from}66`} />
                    )}
                  </div>
                </div>

                {(isActive || isDone) && !isCollapsed && (
                  <div style={{ padding: "10px 11px 12px" }}>
                    {substeps.map((sub, idx) => (
                      <div key={idx} style={{ marginBottom: idx < substeps.length - 1 ? 14 : 0 }}>
                        {/* Directly show the control's label — no image step, no A/B lettering */}
                        <span style={{
                          fontFamily: FONT_BODY, fontStyle: "normal", fontSize: "clamp(9px,0.82vw,11px)",
                          fontWeight: 700, color: "#cde0f2",
                          display: "block", marginBottom: 4,
                        }}>
                          {sub.label}
                        </span>
                        {renderSubstepControl(sub, step)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ══ Admin panel — status only (sliders now live in Step 4) ══ */}
        {showAdminPanel && (
          <div style={{ animation: "adminSlideUp 0.4s cubic-bezier(0.4,0,0.2,1)", flexShrink: 0 }}>
            <AdminPanel
              adminEyeOverride={adminEyeOverride}
              onAdminEyeOverrideChange={onAdminEyeOverrideChange ?? (() => {})}
              dreamingActive={dreamingActive}
              dreamAudioPaused={dreamAudioPaused}
              onPlayAffirmationNow={onPlayAffirmationNow ?? (() => {})}
              onStopAffirmation={onStopAffirmation ?? (() => {})}
              dataEyeState={dataEyeState}
              sessionActive={sessionActive ?? false}
              remDebugEnabled={remDebugEnabled}
              onRemDebugToggle={onRemDebugToggle ?? (() => {})}
            />
          </div>
        )}

        {/* ── Footer ── */}
        <div style={S.footer}>
          <span style={{
            fontFamily: MONO, fontSize: "clamp(7px,0.60vw,9px)",
            color: allDone ? `${ACCENT}cc` : "rgba(120,210,210,0.50)",
            fontStyle: "italic", letterSpacing: 0.5,
          }}>
            {allDone ? "Setup complete" : `${activeStep} / ${STEPS.length} · ${current?.tag}`}
          </span>

          <div style={{ display: "flex", gap: 6 }}>
            {!allDone && (
              <button onClick={handleBack} disabled={activeStep === 1} style={{
                padding: "6px 11px", borderRadius: 5,
                border: `1px solid ${current?.from ?? ACCENT}33`,
                background: "rgba(255,255,255,0.02)",
                fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 1,
                color: activeStep === 1 ? `${current?.from ?? ACCENT}25` : `${current?.from ?? ACCENT}99`,
                cursor: activeStep === 1 ? "not-allowed" : "pointer", transition: "all 0.2s",
              }}>
                ←
              </button>
            )}

            <button
              onClick={sessionActive ? handleEndSession : (allDone ? onBeginSession : handleNext)}
              disabled={!sessionActive && !allDone && nextDisabled}
              style={{
                padding: "6px 13px", borderRadius: 5, border: "none",
                background: sessionActive
                  ? "linear-gradient(90deg, #ef4444, #c02020)"
                  : allDone
                    ? "linear-gradient(90deg, #3ecb8a, #378add)"
                    : nextDisabled ? `${current?.from ?? ACCENT}18`
                    : `linear-gradient(90deg, ${current?.from ?? ACCENT}, ${current?.to ?? ACCENT})`,
                color: !sessionActive && !allDone && nextDisabled ? `${current?.from ?? ACCENT}40` : "#e8f4ff",
                cursor: !sessionActive && !allDone && nextDisabled ? "not-allowed" : "pointer",
                boxShadow: !sessionActive && !allDone && nextDisabled ? "none" : `0 2px 10px ${current?.from ?? ACCENT}44`,
                fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 1,
                transition: "all 0.3s",
              }}>
              {sessionActive ? "⏹ END SESSION" : allDone ? "▶ BEGIN SESSION" : isLastStep ? "FINISH" : "NEXT →"}
            </button>
          </div>
        </div>

      </aside>
    </>
  );
};

export default SetupPane;